/* eslint-disable no-console */
import * as Sentry from '@sentry/react';
import { User } from 'lib/User';
import { ENV_DEV } from 'constants/environment-constants';
import {
    ConfigureScopeParams,
    CustomErrorInstance,
    ErrorHandler,
    ErrorLevel,
    SentryFunction,
    UserType,
} from 'types/error.types';

export function CustomError(): CustomErrorInstance {
    Sentry.init({
        dsn: 'https://04a3abe889b94e67aca6431e462877c2@o79738.ingest.sentry.io/1859142',
        environment: window.__TOTENV__.ENV,
    });

    return {
        fatal: HandleError('fatal', true),
        error: HandleError('error', true),
        warn: HandleError('warning', false),
        info: HandleError('info', false),
        debug: HandleError('debug', false),
    };
}

export function ConfigureScope(
    { user, errorInfo = {}, errorContext = 'global', level }: ConfigureScopeParams,
    sentryFunction: SentryFunction,
    error: unknown,
    callback?: (errorId: string) => void,
) {
    return function extraInfoSetter(scope: Sentry.Scope) {
        scope.setUser(ConfigureUser(user));
        // Map custom ErrorLevel to Sentry's SeverityLevel
        const severityMap: Record<ErrorLevel, Sentry.SeverityLevel> = {
            fatal: 'fatal',
            error: 'error',
            critical: 'error', // Sentry does not have 'critical', map to 'error'
            warning: 'warning',
            info: 'info',
            debug: 'debug',
        };
        const sentryLevel: Sentry.SeverityLevel = severityMap[(level ?? 'error') as ErrorLevel];
        scope.setLevel(sentryLevel);
        scope.setTag('username', user?.username);
        scope.setTag('error-context', errorContext);
        scope.setExtras({
            ...errorInfo,
        });

        let errorId: string | undefined;

        if (window.__TOTENV__.ENV && window.__TOTENV__.ENV !== ENV_DEV) {
            errorId = sentryFunction(error);
        } else {
            errorId = `test-${Math.random().toString(36).substr(2)}`;
        }
        if (callback) callback(errorId as string);
    };
}

function ConfigureUser(user?: UserType): Record<string, any> {
    let userScope: Record<string, any> = {};

    if (user) {
        const {
            _id: id,
            emails,
            profile: { firstname, lastname, organizationId },
            username,
        } = user;
        const email = emails?.[0]?.address;
        userScope = {
            ...userScope,
            id,
            organizationId,
            username,
            email,
            name: `${firstname} ${lastname}`,
        };
    }

    return userScope;
}

function HandleError(type: ErrorLevel, shouldThrow = false): ErrorHandler {
    return function ErrorHandlerInstance(
        error: unknown,
        errorInfo: Record<string, any> = {},
        callback?: (errorId: string) => void,
        shouldThrowForInstance: boolean = type === 'fatal' || type === 'error' || type === 'critical',
    ) {
        let errorTranspose = error;
        let sentryFunction: SentryFunction;

        if (typeof error === 'string' && type !== 'debug') {
            sentryFunction = Sentry.captureEvent as SentryFunction;
            errorTranspose = {
                message: error,
            };
        } else if (typeof error === 'string' && type === 'debug') {
            sentryFunction = Sentry.captureMessage as SentryFunction;
        } else {
            sentryFunction = Sentry.captureException as SentryFunction;
        }

        let consoleType: keyof typeof console = type as keyof typeof console;

        if (type === 'critical' || type === 'fatal') {
            consoleType = 'error';
        } else if (type === 'warning') {
            consoleType = 'warn';
        }

        // @ts-ignore
        console[consoleType](error);

        Sentry.withScope(
            ConfigureScope(
                {
                    errorInfo,
                    user: User.get(),
                    errorContext: 'logger',
                    level: type,
                },
                sentryFunction,
                errorTranspose,
                callback,
            ),
        );

        if (shouldThrowForInstance || (shouldThrow && shouldThrowForInstance)) {
            throw error;
        }
    };
}
